import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../animations/PageTransition';
import { motion } from 'framer-motion';
import { VscArrowDown } from 'react-icons/vsc';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { Socials } from './Socials';
import { BsArrowRight } from 'react-icons/bs';
import { FAQToggle } from './Toggle';

export const StudioSingle = ({ transition, title, preamble, studioNumber, slider, gallery, floorplan, loadingbay, contact, specification, equipment, area, faq }) => {
	return (
		<motion.main className="studio-single-main" exit={{ opacity: 0 }} transition={transition}>
			<PageTransition />
			{/* HERO */}
			<section className="section section-studio-single-hero">
				<div className="row">
					<div className="col col-7">
						<div className="module-text">
							<h1 className="studio-single-hero-header-title" data-text={title}>
								{title}
							</h1>
							<p className="lg">{preamble}</p>
						</div>
					</div>
					<div className="col col-5">
						<div className="studio-single-hero-studio-number">
							<span className="studio-number" data-text={studioNumber}>
								{studioNumber}
							</span>
						</div>
					</div>
				</div>
				<div className="row row-hero-arrow">
					<div className="col col-12">
						<VscArrowDown className="studio-single-hero-arrow" />
					</div>
				</div>
			</section>
			{/* SLIDER */}
			{slider && (
				<section className="section studio-single-slider-sec">
					<div className="row img-mobile-first">
						<div className="col col-5">
							<div className="module-text">
								<h2 data-text={slider.title}>{slider.title}</h2>
								<p>{slider.text}</p>
							</div>
						</div>
						<div className="col col-7">
							<div className="module-slider variable-width">
								<Swiper
									modules={[Autoplay, Pagination]}
									slidesPerView="auto"
									loop
									speed={1200}
									autoplay={{
										delay: 4800,
										disableOnInteraction: false,
									}}
									pagination={{ clickable: true }}
									grabCursor
									resistanceRatio={0.6}
								>
									{slider.items.map((img, i) => (
										<SwiperSlide key={i}>
											<picture>
												<source type="image/webp" srcSet={img.webp.srcSet} sizes="(max-width: 768px) 100vw, 60vw" />
												<source type="image/jpeg" srcSet={img.jpg.srcSet} sizes="(max-width: 768px) 100vw, 60vw" />
												<img src={img.jpg.src} alt="" loading="lazy" decoding="async" />
											</picture>
										</SwiperSlide>
									))}
								</Swiper>
							</div>
						</div>
					</div>
				</section>
			)}
			{/* GALLERY */}
			{gallery && (
				<section className="section studio-single-image-grid-sec">
					<div className="row">
						<div className="col col-12 offset-r-5">
							<div className="module-text">
								<h2>{gallery.title}</h2>
								<p>{gallery.text}</p>
							</div>
						</div>
					</div>

					<div className="row">
						<div className="col col-12">
							<div className="studio-single-photo-grid-container">
								{gallery.items.map((img, i) => (
									<picture key={i}>
										<source type="image/webp" srcSet={img.webp.srcSet} sizes="(max-width: 768px) 50vw, 33vw" />
										<source type="image/jpeg" srcSet={img.jpg.srcSet} sizes="(max-width: 768px) 50vw, 33vw" />
										<img src={img.jpg.src} alt="" loading="lazy" decoding="async" />
									</picture>
								))}
							</div>
						</div>
					</div>
				</section>
			)}
			{specification && equipment && (
				<section className="section section-studio-single-specifications">
					<div className="row">
						<div className="col col-5 offset-l-1">
							<div className="module-text">
								<h2>{specification.title}</h2>
								<p>{specification.text}</p>
							</div>
						</div>
						<div className="col col-5 offset-r-1">
							<div className="module-text">
								<h2>{equipment.title}</h2>
								<p>{equipment.text}</p>
							</div>
						</div>
					</div>
				</section>
			)}
			{/* FLOORPLAN / LOADING BAY / AREA */}
			{(floorplan || loadingbay || area) && (
				<section className="section section-studio-single-info">
					{loadingbay && (
						<div className="row img-mobile-first row-card">
							<div className="col col-5">
								<div className="module-text">
									<h2 data-text={loadingbay.title}>{loadingbay.title}</h2>
									<p>{loadingbay.text}</p>
								</div>
							</div>
							<div className="col col-7">
								<picture>
									<source type="image/webp" srcSet={loadingbay.image.webp.srcSet} sizes="100vw" />
									<source type="image/jpeg" srcSet={loadingbay.image.jpg.srcSet} sizes="100vw" />
									<img src={loadingbay.image.jpg.src} alt="loadingbay" loading="lazy" />
								</picture>
							</div>
						</div>
					)}

					{floorplan && (
						<div className="row row-card">
							<div className="col col-7">
								<picture>
									<source type="image/webp" srcSet={floorplan.image.webp.srcSet} sizes="100vw" />
									<source type="image/jpeg" srcSet={floorplan.image.jpg.srcSet} sizes="100vw" />
									<img src={floorplan.image.jpg.src} alt="floorplan" loading="lazy" />
								</picture>
							</div>
							<div className="col col-5">
								<div className="module-text">
									<h2 data-text={floorplan.title}>{floorplan.title}</h2>
									<p>{floorplan.text}</p>
								</div>
							</div>
						</div>
					)}
					{area && (
						<div className="row img-mobile-first row-card">
							<div className="col col-5">
								<div className="module-text">
									<h2 data-text={area.title}>{area.title}</h2>
									<p>{area.text}</p>
								</div>
							</div>
							<div className="col col-7">
								<picture>
									<source type="image/webp" srcSet={area.image.webp.srcSet} sizes="100vw" />
									<source type="image/jpeg" srcSet={area.image.jpg.srcSet} sizes="100vw" />
									<img src={area.image.jpg.src} alt="area" loading="lazy" />
								</picture>
							</div>
						</div>
					)}
				</section>
			)}

			{faq && (
				<section className="section section-studio-single-faq">
					<div className="row">
						<div className="col col-12 offset-l-2 offset-r-2">
							<div className="module-text">
								<h2>{faq.title}</h2>
								<p className="md">{faq.text}</p>
							</div>
						</div>
					</div>
					<div className="row">
						<div className="col col-12 offset-l-2 offset-r-2">
							<FAQToggle
								question="What types of projects can I do at Spring Studio?"
								answer="Spring Studio is perfect for photography, film, commercial productions, music rehearsals, and all kinds of creative projects or festivities."
							/>

							<FAQToggle
								question="How large is the studio?"
								answer="The studio is 640 m² with a ceiling height of 4-5 meters, providing plenty of space for sets, equipment, and groups."
							/>

							<FAQToggle
								question="Is the studio equipped for long shoots or large teams?"
								answer="Yes! We have a kitchen, toilet, shower, and multiple seating areas for work or relaxation, making it easy to accommodate your crew."
							/>

							<FAQToggle
								question="How do I get to Spring Studio?"
								answer="Spring Studio is just a three-minute walk from Örnsbergs metro station in Aspudden/Örnsberg, Stockholm. Our address is Instrumentvägen 29, 126 53 Örnsberg, Stockholm."
							/>

							<FAQToggle
								question="Can I deliver large equipment or props to the studio?"
								answer="Absolutely! The studio has a connected loading bay and elevators for easy delivery and setup."
							/>

							<FAQToggle question="How do I book the studio?" answer="You can book the studio by sending an email to janne@springstudio.se." />
						</div>
					</div>
				</section>
			)}
			{/* CONTACT */}
			{contact && (
				<section className="section section-studio-single-contact">
					<div className="row theme-dark">
						<div className="col col-6">
							<div className="module-text">
								<h2 className="fade d1" data-text={contact.title}>
									{contact.title}
								</h2>
								<p className="fade sm">{contact.text}</p>
							</div>

							<div className="studio-single-socials-container">
								<Socials />
							</div>
						</div>

						<div className="col col-6">
							<Link to="/contact" className="btn btn-secondary">
								{contact.ctaTitle} <BsArrowRight />
							</Link>
						</div>
					</div>
				</section>
			)}
		</motion.main>
	);
};
